import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/router';
import { useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'next-i18next';
import { API, APIRoutes } from '@utils/api';
import routes from '@utils/routes';

import {
  CompanyDetails,
  DetailWrapper,
  Header,
  Content,
  Address,
} from '@components/EmployerProfile/CompanyData/StaticMode/styles';

import { Pagination } from '@styles/globalStyles';
import { Container, Wrapper, Description, Offers } from '@components/EmployerDetails/styles';
import OfferBox from '@components/OfferBox';
import TextHeader, { SIZE, WEIGHT, TAG } from '@components/TextHeader';
import Link from '@components/Link';

function Employer({ slug, offers }) {
  const { t } = useTranslation('employerProfile');

  const { employerProfile: profile } = slug;
  const {
    meta: {
      pagination: { currentPage, pages },
    },
  } = offers || {};

  const fetchOffers = useCallback(
    async (pg = 1, sg = profile.slug) => {
      const { data } = await API.get(APIRoutes.employerOffers({ page: pg, slug: sg }));
      return data;
    },
    [profile.slug]
  );

  const [page, setPage] = useState(parseInt(currentPage, 10));

  const offersRef = useRef(null);
  const isMounted = useRef(false);
  const router = useRouter();
  const queryClient = useQueryClient();

  const { data, isFetching } = useQuery(['offers', page], () => fetchOffers(page, profile.slug), {
    enabled: false,
    placeholderData: offers,
    keepPreviousData: true,
  });

  useEffect(() => {
    if (isMounted.current) {
      queryClient.prefetchQuery(['offers', page], () => fetchOffers(page, profile.slug));
    } else {
      isMounted.current = true;
    }
  }, [queryClient, page, profile.slug, fetchOffers]);

  const handlePageClick = async (event) => {
    const pageNumber = event.selected + 1;
    await router.replace(`/pracodawca/${profile.slug}/?page=${pageNumber}`, undefined, { shallow: true });
    setPage(pageNumber);

    const navOffset = 150;
    const ref = offersRef.current?.getBoundingClientRect().top;
    offersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    const offsetPosition = ref + window.pageYOffset - navOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  };

  return (
    <Wrapper>
      <Container>
        <CompanyDetails>
          <DetailWrapper>
            {profile.logo.desktop ? (
              <img src={profile.logo.desktop} alt={`logo ${profile.name}`} />
            ) : (
              <p>Brak zdjęcia</p>
            )}
          </DetailWrapper>
          <DetailWrapper>
            <TextHeader tag={TAG.H2} size={SIZE.MD} weight={WEIGHT.MEDIUM}>
              {profile.name}
            </TextHeader>
            <Header>{profile.fieldOfExpertise}</Header>
            <Content>
              {profile.street} {profile.streetNo}
            </Content>
            <Content>
              {profile.postCode}, {profile.city}
            </Content>
            <Content>tel: {profile.phoneNumber}</Content>
            <br />
            {profile.website && (
              <Content>
                <Link href={profile.website.match(/^http(s)?:\/\//) ? profile.website : `https://${profile.website}`}>
                  {profile.website}
                </Link>
              </Content>
            )}
          </DetailWrapper>
        </CompanyDetails>
        <TextHeader tag={TAG.H3} size={SIZE.SM} weight={WEIGHT.BOLD}>
          {t('Company description')}
        </TextHeader>
        <Description dangerouslySetInnerHTML={{ __html: profile.description }} />
        <TextHeader tag={TAG.H3} size={SIZE.SM} weight={WEIGHT.BOLD}>
          Lista Ofert
        </TextHeader>
        <Offers ref={offersRef}>
          {data?.offers && data.offers.length ? (
            data?.offers.map((offer) => (
              <OfferBox key={`offer-box_${offer.id}`} offer={offer} isFetching={isFetching} />
            ))
          ) : (
            <p>Aktualnie brak aktywnych ofert :(</p>
          )}
        </Offers>
        <Pagination
          className="pagination"
          breakLabel="..."
          nextLabel="next"
          onPageChange={handlePageClick}
          pageRangeDisplayed={2}
          initialPage={page - 1}
          disableInitialCallback
          pageCount={pages}
          previousLabel="previous"
          renderOnZeroPageCount={null}
        />
      </Container>
    </Wrapper>
  );
}

export default Employer;
